import { ROLE_LABELS, type Role } from '@lia/contracts';
import { AsyncErrorView, AsyncState, type AsyncErrorInfo } from '@lia/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import { ApiError } from '../lib/api.js';

// Thin adapters from TanStack Query + ApiError to the API-agnostic AsyncState / AsyncErrorView in @lia/ui.

/** Maps any thrown error (ApiError or not) to the library's error shape. */
export function toAsyncError(error: unknown): AsyncErrorInfo {
  if (error instanceof ApiError && error.kind === 'forbidden')
    return { kind: 'forbidden', message: error.message };
  if (error instanceof ApiError && error.kind === 'not_found')
    return { kind: 'not_found', message: error.message };
  return {
    kind: 'error',
    message: error instanceof Error ? error.message : undefined,
    reference:
      error instanceof ApiError
        ? `${error.status || 'red'} · ${error.code}`
        : undefined,
  };
}

interface CommonProps {
  /** Role shown in the permission-denied message. */
  requiredRole?: Role;
  /** Title for 404s (resource missing or not accessible). */
  notFoundTitle?: string;
  errorTitle?: string;
  compact?: boolean;
}

export interface QueryStateProps<T> extends CommonProps {
  query: UseQueryResult<T, ApiError>;
  children: (data: T) => React.ReactNode;
  /** Loading placeholder (defaults to a text skeleton). */
  loading?: React.ReactNode;
}

/** Renders loading / permission-denied / not-found / error-with-retry around a query, then the data. */
export function QueryState<T>({
  query,
  requiredRole,
  ...rest
}: QueryStateProps<T>) {
  return (
    <AsyncState
      status={query.status}
      data={query.data}
      error={query.isError ? toAsyncError(query.error) : null}
      onRetry={() => void query.refetch()}
      retrying={query.isFetching}
      requiredRole={requiredRole ? ROLE_LABELS[requiredRole] : undefined}
      {...rest}
    />
  );
}

export interface ApiErrorViewProps extends CommonProps {
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
}

/** Maps an API error to the right state component. 404 on a route that is still being built shows honestly. */
export function ApiErrorView({
  error,
  requiredRole,
  ...rest
}: ApiErrorViewProps) {
  return (
    <AsyncErrorView
      error={toAsyncError(error)}
      requiredRole={requiredRole ? ROLE_LABELS[requiredRole] : undefined}
      {...rest}
    />
  );
}
